import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format large numbers with commas
export function formatNumber(num, decimals = 2) {
  if (num === undefined || num === null) return '0';
  return Number(num).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}

// Format hash for display (truncate middle)
export function formatHash(hash, startLen = 8, endLen = 8) {
  if (!hash || hash.length <= startLen + endLen) return hash || '';
  return `${hash.slice(0, startLen)}...${hash.slice(-endLen)}`;
}

// Format address for display
export function formatAddress(address, startLen = 10, endLen = 8) {
  if (!address || address.length <= startLen + endLen) return address || '';
  return `${address.slice(0, startLen)}...${address.slice(-endLen)}`;
}

// Format time ago
export function timeAgo(timestamp) {
  const now = new Date();
  const time = new Date(timestamp);
  const diff = Math.floor((now - time) / 1000);

  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return time.toLocaleDateString();
}

// Format date
export function formatDate(timestamp) {
  return new Date(timestamp).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// Format GG amount
export function formatGG(amount, decimals = 6) {
  if (amount === undefined || amount === null) return '0 GG';
  return `${formatNumber(amount, decimals)} GG`;
}

// Format hashrate
export function formatHashrate(hashrate) {
  if (!hashrate) return '0 H/s';
  const units = ['H/s', 'KH/s', 'MH/s', 'GH/s', 'TH/s', 'PH/s', 'EH/s'];
  let unitIndex = 0;
  let value = hashrate;
  
  while (value >= 1000 && unitIndex < units.length - 1) {
    value /= 1000;
    unitIndex++;
  }
  
  return `${formatNumber(value, 2)} ${units[unitIndex]}`;
}

// Format bytes
export function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let value = bytes;
  
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  
  return `${formatNumber(value, 2)} ${units[unitIndex]}`;
}

// Copy to clipboard
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
}

// Calculate epoch progress
export function getEpochProgress(currentHeight, blocksPerEpoch = 12614400) {
  const currentEpoch = Math.floor(currentHeight / blocksPerEpoch) + 1;
  const blocksInCurrentEpoch = currentHeight % blocksPerEpoch;
  const progress = (blocksInCurrentEpoch / blocksPerEpoch) * 100;
  const blocksRemaining = blocksPerEpoch - blocksInCurrentEpoch;
  
  return {
    epoch: currentEpoch,
    progress: progress.toFixed(2),
    blocksInEpoch: blocksInCurrentEpoch,
    blocksRemaining,
    blocksPerEpoch,
  };
}

// Get transaction type color
export function getTxTypeColor(type) {
  const colors = {
    transfer: 'text-primary',
    stake: 'text-green-400',
    unstake: 'text-yellow-400',
    contract: 'text-purple-400',
    reward: 'text-cyan-400',
  };
  return colors[type] || 'text-muted-foreground';
}

// Generate gradient based on hash
export function hashToGradient(hash) {
  if (!hash) return 'linear-gradient(135deg, #00F0FF 0%, #7000FF 100%)';
  const hue1 = parseInt(hash.slice(0, 6), 16) % 360;
  const hue2 = (hue1 + 60) % 360;
  return `linear-gradient(135deg, hsl(${hue1}, 70%, 50%) 0%, hsl(${hue2}, 70%, 50%) 100%)`;
}
